import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../../axios';
import { GET_BOOKING_DATA } from '../../api';
import { baseImageUrl } from '../../utils/config';
import Logo from '../../../assets/logo.png';

const Page = styled(Box)(({ theme }) => ({
  minHeight: '100%',
  padding: '22px 20px 30px',
  background: '#f7faff',
  [theme.breakpoints.down('sm')]: { padding: '16px 12px 24px' },
}));

const Card = styled(Paper)(() => ({
  border: '1px solid #dbe7f3',
  borderRadius: 10,
  boxShadow: '0 3px 12px rgba(15, 52, 96, 0.06)',
  overflow: 'hidden',
}));

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;
const mediaUrl = (value) => {
  if (!value) return '/assets/images/Sunlit Living Room Cleaning Scene.png';
  if (/^(https?:)?\/\//i.test(value)) return value;
  return `${baseImageUrl}${value}`;
};

function ServicePreview() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    axios
      .get(`${GET_BOOKING_DATA}/${slug}`)
      .then((response) => {
        if (!active) return;
        const data = response?.data?.data;
        if (!data) throw new Error('Service preview data was not found.');
        const packages = (data.packages || []).slice().sort((a, b) => a.order - b.order);
        setService({ ...data, packages });
        setSelectedItems(
          packages.reduce((result, packageData) => {
            const firstItem = (packageData?.item || [])[0];
            if (firstItem) result[packageData.id] = firstItem;
            return result;
          }, {})
        );
      })
      .catch(() => active && setError('Service preview could not be loaded.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [slug]);

  const chosenItems = useMemo(() => Object.values(selectedItems).filter(Boolean), [selectedItems]);
  const subtotal = chosenItems.reduce((sum, item) => sum + (Number(item?.price) || 0), 0);
  const extrasTotal = selectedExtras.reduce((sum, item) => sum + (Number(item?.price) || 0), 0);
  const taxRate = Number(service?.tax?.[0]?.tax_rate ?? service?.tax?.tax_rate ?? 0) || 0;
  const tax = ((subtotal + extrasTotal) * taxRate) / 100;
  const total = subtotal + extrasTotal + tax;
  const displayDate = new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }).format(new Date());

  const toggleExtra = (extra) => {
    setSelectedExtras((current) =>
      current.some((item) => item.id === extra.id)
        ? current.filter((item) => item.id !== extra.id)
        : [...current, extra]
    );
  };

  return (
    <Page>
      <Typography sx={{ fontSize: 11, color: '#718096', mb: 1 }}>
        Dashboard&nbsp; › &nbsp;Services&nbsp; › &nbsp;{service?.name || 'Service'}&nbsp; › &nbsp;Preview
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={1.5}>
        <Box>
          <Typography sx={{ fontSize: 21, lineHeight: 1.2, fontWeight: 800, color: '#102a56' }}>Service Booking Preview</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.3 }}>This is a preview of how this service will appear to customers. No booking will be created.</Typography>
        </Box>
        <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate('/dashboard/services')} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
          Back to Services
        </Button>
      </Stack>

      <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.15, mb: 2, borderRadius: 2, bgcolor: '#dff2ff', border: '1px solid #c7e8fb', color: '#174d78', fontSize: 12, fontWeight: 600 }}>
        <InfoOutlinedIcon sx={{ fontSize: 19, color: '#1686d9' }} />
        <strong>Preview Mode</strong>&nbsp; This is a test preview of the customer booking experience. No booking will be created, no payment will be charged, and no confirmation email will be sent.
      </Box>

      {loading && <Box sx={{ py: 12, textAlign: 'center' }}><CircularProgress size={34} /></Box>}
      {!loading && error && <Alert severity="error" action={<Button color="inherit" onClick={() => navigate('/dashboard/services')}>Back</Button>}>{error}</Alert>}

      {!loading && !error && service && (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} lg={8.2}>
            <Card>
              <Box sx={{ position: 'relative', height: { xs: 250, md: 330 }, bgcolor: '#eaf5fb' }}>
                <Box component="img" src="/assets/images/Sunlit Living Room Cleaning Scene.png" alt="Sunlit living room cleaning service" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(255,255,255,.96) 0%, rgba(255,255,255,.76) 34%, rgba(255,255,255,0) 67%)' }} />
                <Box sx={{ position: 'absolute', left: { xs: 22, md: 42 }, top: { xs: 28, md: 52 }, maxWidth: 290 }}>
                  <Box component="img" src={Logo} alt="Cleany" sx={{ width: 150, height: 'auto', mb: 1.5 }} />
                  <Typography sx={{ color: '#075b8f', fontSize: { xs: 26, md: 34 }, lineHeight: 1.06, fontWeight: 800 }}>A Cleaner Home.<br />A Better Day.</Typography>
                  <Typography sx={{ mt: 1.3, color: '#356277', fontSize: 12, fontWeight: 600 }}>Professional home cleaning,<br />just a few clicks away.</Typography>
                </Box>
              </Box>
              <Grid container sx={{ bgcolor: '#e8f7ff', borderTop: '1px solid #cfe8f5' }}>
                {[
                  [<VerifiedUserOutlinedIcon />, '100% Satisfaction', 'Guaranteed'],
                  [<CalendarTodayOutlinedIcon />, 'Easy Online', 'Booking'],
                  [<CheckCircleOutlineIcon />, 'Trusted Cleaners.', 'Quality You Can See.'],
                  [<SupportAgentOutlinedIcon />, 'Live Customer', 'Support'],
                ].map(([icon, title, subtitle]) => (
                  <Grid item xs={6} sm={3} key={title} sx={{ p: 1.3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'grid', placeItems: 'center', width: 31, height: 31, borderRadius: '50%', bgcolor: '#0b91df', color: '#fff', '& svg': { fontSize: 18 } }}>{icon}</Box>
                    <Box><Typography sx={{ fontSize: 10, fontWeight: 800, color: '#16618e' }}>{title}</Typography><Typography sx={{ fontSize: 9, color: '#4f7890' }}>{subtitle}</Typography></Box>
                  </Grid>
                ))}
              </Grid>
            </Card>

            <Typography sx={{ mt: 2.2, mb: 1, fontSize: 15, fontWeight: 800, color: '#102a56' }}>Select Your Service Options</Typography>
            <Stack spacing={1}>
              {(service.packages || []).map((packageData) => (
                <Card key={packageData.id} sx={{ p: 1.2 }}>
                  <Typography sx={{ mb: 0.8, fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>{packageData.title}</Typography>
                  <Grid container spacing={1}>
                    {(packageData.item || []).map((item) => {
                      const selected = selectedItems[packageData.id]?.id === item.id;
                      return (
                        <Grid item xs={12} md={6} key={item.id}>
                          <Box onClick={() => setSelectedItems((current) => ({ ...current, [packageData.id]: item }))} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.2, p: 1, borderRadius: 2, border: `1px solid ${selected ? '#3b9ee8' : '#e1e8f0'}`, bgcolor: selected ? '#f0f8ff' : '#fff' }}>
                            <Checkbox size="small" checked={selected} tabIndex={-1} inputProps={{ readOnly: true, 'aria-label': `Select ${item.title || item.name || 'service option'}` }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}><Typography sx={{ fontSize: 12, fontWeight: 800, color: '#183153' }}>{item.title || item.name}</Typography><Typography sx={{ fontSize: 10, color: '#718096' }}>{item.time_hrs || 0} hours (estimated)</Typography></Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#183153' }}>{money(item.price)}</Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Card>
              ))}
            </Stack>

            {!!service.extras?.length && <Typography sx={{ mt: 2.2, mb: 1, fontSize: 15, fontWeight: 800, color: '#102a56' }}>Add Extras <Box component="span" sx={{ fontWeight: 500, color: '#64748b' }}>(Optional)</Box></Typography>}
            <Grid container spacing={1}>
              {(service.extras || []).map((extra) => {
                const selected = selectedExtras.some((item) => item.id === extra.id);
                return (
                  <Grid item xs={12} sm={6} md={4} key={extra.id}>
                    <Card onClick={() => toggleExtra(extra)} sx={{ p: 1, cursor: 'pointer', borderColor: selected ? '#3b9ee8' : '#dbe7f3', bgcolor: selected ? '#f0f8ff' : '#fff' }}>
                      <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-start' }}><Checkbox size="small" checked={selected} tabIndex={-1} inputProps={{ readOnly: true, 'aria-label': `Select ${extra.title || 'extra'}` }} /><Box component="img" src={mediaUrl(extra.image_12 || extra.image)} alt="" sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover' }} /><Box><Typography sx={{ fontSize: 11, fontWeight: 800, color: '#183153' }}>{extra.title}</Typography><Typography sx={{ fontSize: 9, color: '#718096' }}>{extra.time_hrs || 0} additional hours</Typography><Typography sx={{ fontSize: 11, fontWeight: 800, color: '#183153' }}>{money(extra.price)}</Typography></Box></Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Typography sx={{ mt: 2.2, mb: 1, fontSize: 15, fontWeight: 800, color: '#102a56' }}>Preferred Date &amp; Time</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}><Card sx={{ px: 1.5, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}><CalendarTodayOutlinedIcon color="primary" sx={{ fontSize: 18 }} /><Box><Typography sx={{ fontSize: 9, color: '#718096' }}>Select Date</Typography><Typography sx={{ fontSize: 12, fontWeight: 700 }}>{displayDate}</Typography></Box></Card></Grid>
              <Grid item xs={12} sm={6}><Card sx={{ px: 1.5, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}><ScheduleOutlinedIcon color="primary" sx={{ fontSize: 18 }} /><Box><Typography sx={{ fontSize: 9, color: '#718096' }}>Select Time</Typography><Typography sx={{ fontSize: 12, fontWeight: 700 }}>9:00 AM</Typography></Box></Card></Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} lg={3.8}>
            <Card sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
              <Typography sx={{ pb: 1.3, mb: 1.3, borderBottom: '1px solid #e5edf5', fontSize: 17, fontWeight: 800, color: '#102a56' }}>Booking Summary</Typography>
              <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center', mb: 1.6 }}>
                <Box component="img" src={mediaUrl(service.image_1)} alt="" sx={{ width: 66, height: 48, objectFit: 'cover', borderRadius: 1.5 }} />
                <Box sx={{ flex: 1 }}><Typography sx={{ fontSize: 12, fontWeight: 800 }}>{service.name}</Typography><Typography sx={{ fontSize: 10, color: '#718096' }}>{service.type || 'Service'}</Typography></Box>
                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{money(subtotal)}</Typography>
              </Box>
              <Stack spacing={1.1} sx={{ py: 1.3, borderTop: '1px solid #e5edf5', borderBottom: '1px solid #e5edf5' }}>
                <Box sx={{ display: 'flex', gap: 1 }}><CalendarTodayOutlinedIcon sx={{ fontSize: 17, color: '#64748b' }} /><Typography sx={{ fontSize: 11 }}>{displayDate}</Typography></Box>
                <Box sx={{ display: 'flex', gap: 1 }}><ScheduleOutlinedIcon sx={{ fontSize: 17, color: '#64748b' }} /><Typography sx={{ fontSize: 11 }}>9:00 AM</Typography></Box>
                <Box sx={{ display: 'flex', gap: 1 }}><PeopleAltOutlinedIcon sx={{ fontSize: 17, color: '#64748b' }} /><Typography sx={{ fontSize: 11 }}>One time</Typography></Box>
              </Stack>
              <Stack spacing={0.8} sx={{ py: 1.5, '& > div': { display: 'flex', justifyContent: 'space-between' } }}>
                <Box><Typography sx={{ fontSize: 11 }}>Service Price</Typography><Typography sx={{ fontSize: 11 }}>{money(subtotal)}</Typography></Box>
                <Box><Typography sx={{ fontSize: 11 }}>Discounts</Typography><Typography sx={{ fontSize: 11 }}>$0.00</Typography></Box>
                <Box><Typography sx={{ fontSize: 11 }}>Extras</Typography><Typography sx={{ fontSize: 11 }}>{money(extrasTotal)}</Typography></Box>
                <Box sx={{ pt: 1, mt: 0.5, borderTop: '1px solid #e5edf5' }}><Typography sx={{ fontSize: 12, fontWeight: 800 }}>Subtotal</Typography><Typography sx={{ fontSize: 12, fontWeight: 800 }}>{money(subtotal + extrasTotal)}</Typography></Box>
                <Box><Typography sx={{ fontSize: 11 }}>Taxes ({taxRate.toFixed(2)}%)</Typography><Typography sx={{ fontSize: 11 }}>{money(tax)}</Typography></Box>
                <Box sx={{ p: 1, mt: 0.5, bgcolor: '#e6f6ff', borderRadius: 1.5 }}><Typography sx={{ fontSize: 15, fontWeight: 900, color: '#102a56' }}>Total</Typography><Typography sx={{ fontSize: 15, fontWeight: 900, color: '#102a56' }}>{money(total)}</Typography></Box>
              </Stack>
              <Box sx={{ display: 'flex', gap: 1, p: 1.2, mb: 1.2, bgcolor: '#dff2ff', borderRadius: 2, color: '#174d78' }}><InfoOutlinedIcon sx={{ fontSize: 18, flex: '0 0 auto' }} /><Typography sx={{ fontSize: 10 }}><strong>Preview Mode</strong><br />This is a preview only. No booking, payment, or email will be created.</Typography></Box>
              <Button fullWidth disabled startIcon={<LockOutlinedIcon />} variant="contained" sx={{ py: 1, textTransform: 'none', fontWeight: 800 }}>Preview Only</Button>
              <Typography sx={{ mt: 1, textAlign: 'center', fontSize: 9, color: '#94a3b8' }}>You cannot complete a booking from here.</Typography>
            </Card>
          </Grid>
        </Grid>
      )}
    </Page>
  );
}

export default ServicePreview;
